/**
 * jsTierPump.js — shared playlist-poll + demux + feed loop for the
 * WebCodecs and WASM decode tiers (PLAYBACK-HANDOFF.md §8 points 2/3).
 * Also doubles as the tier-agnostic buffered-duration/completion watcher
 * (`trackOnly: true`) used ALONGSIDE the MSE/hls.js tier -- see that flag's
 * doc below for why hls.js's own reload/duration reporting isn't trusted
 * for that job.
 *
 * Ported from mock FE `beginJsPlayback()`. Drives ANY backend implementing
 * `{configure({width,height,fps}), feed(annexB,isKey,ptsUs), pause(),
 * resume(), get paused(), destroy()}` — webcodecsBackend.js and
 * wasmBackend.js differ only in that object, this pump is identical for
 * both.
 *
 * A single failed chunk GET does not kill the pump: it un-marks that URI
 * and lets the next poll retry it (a 401 at a token boundary, or an S3
 * object listed a moment before it is readable, must not end playback).
 */
import { parsePlaylist } from '../storageClient';
import { parseInit, parseMediaChunk } from './demux';

const POLL_DELAY_MS = 1000;
const IDLE_REPOLL_MS = 5000;

function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

/**
 * @param {object} args
 * @param {string} args.playlistUrl
 * @param {() => string} args.getToken
 * @param {object} [args.backend] required unless `trackOnly`
 * @param {object} args.callbacks
 *   onBufferedSeconds(totalDurationSec), onFirstAuFed(), onEndlist(), onError(err)
 * @param {number} [args.expectedDurationSec] the segment's known full length
 *   (from playback_ack/cache row duration_ms) -- see the stop-condition note
 *   on IDLE_REPOLL_MS below. Omit only when truly unknown.
 * @param {boolean} [args.trackOnly] skip the demux/decoder-feed path
 *   entirely -- just poll the playlist and report onBufferedSeconds/
 *   onEndlist. Used to run this pump ALONGSIDE the MSE/hls.js tier, whose
 *   OWN reload timer turned out not to be a reliable source for "how much
 *   of this segment exists in storage": measured 2026-09-12 against real
 *   still-uploading (`live:true`, no seal at all) segments, hls.js loaded
 *   the manifest exactly once and then silently never reloaded again for
 *   the rest of the transfer (no error, no logged event of any kind) --
 *   this is NOT the same bug as the ENDLIST-short case below, and it isn't
 *   worth chasing inside hls.js's own live-playlist reload heuristics for
 *   our (unusual: never drops old segments, keeps growing from #1) manifest
 *   shape. Cheaper and more robust to never depend on it: run this
 *   already-proven poll loop unconditionally and let it own bufferedSec.
 * @returns {{ stop(): void }}
 */
export function startJsTierPump({ playlistUrl, getToken, backend, callbacks, expectedDurationSec, trackOnly }) {
  const { onBufferedSeconds, onFirstAuFed, onEndlist, onError } = callbacks || {};
  let stopped = false;
  let initInfo = null;
  let fedFirstAu = false;
  const fedUris = new Set();
  let mapUri = null;
  let endlistSeen = false;
  let notReadyPolls = 0; // diagnostic only -- see the log below

  function authHeaders() {
    const t = getToken();
    return t ? { Authorization: 'Bearer ' + t } : {};
  }
  function fetchBytes(url) {
    return fetch(url, { cache: 'no-store', headers: authHeaders() }).then((resp) => {
      if (!resp.ok) throw new Error(`HTTP ${resp.status} tải ${url}`);
      return resp.arrayBuffer().then((buf) => new Uint8Array(buf));
    });
  }
  function ensureInit(uri) {
    if (initInfo) return Promise.resolve();
    console.info('[jsTierPump] fetching init chunk', uri);
    return fetchBytes(uri).then((bytes) => {
      initInfo = parseInit(bytes);
      console.info('[jsTierPump] init parsed, configuring decoder', initInfo);
      // webcodecsBackend.configure() is synchronous (returns undefined);
      // wasmBackend.configure() returns a Promise -- Promise.resolve()
      // normalizes both instead of assuming either shape.
      return Promise.resolve(backend.configure({ width: initInfo.width, height: initInfo.height, fps: 25 })).then(() => {
        console.info('[jsTierPump] decoder configured');
      });
    });
  }
  function feedMediaUri(uri) {
    return fetchBytes(uri).then((bytes) => {
      if (!initInfo) throw new Error('nhận media chunk trước init chunk');
      const aus = parseMediaChunk(bytes, initInfo);
      for (let i = 0; i < aus.length; i++) {
        const au = aus[i];
        if (!fedFirstAu) {
          if (!au.isKey) continue; // first fed AU must be a keyframe
          fedFirstAu = true;
          onFirstAuFed && onFirstAuFed();
        }
        backend.feed(au.annexB, au.isKey, au.ptsUs);
      }
    });
  }

  function tick() {
    if (stopped) return Promise.resolve();
    return fetch(playlistUrl, { cache: 'no-store', headers: authHeaders() }).then((resp) => {
      if (!resp.ok) {
        notReadyPolls += 1;
        // Throttled: this is the expected, silent-by-design steady state
        // while waiting for the device's upload to reach storage (can be a
        // few seconds for a fresh clip) -- but it must NOT be silent forever,
        // or a genuine stall (storage/relay never sees this segment at all)
        // looks identical to a normal short wait.
        if (notReadyPolls === 1 || notReadyPolls % 10 === 0) {
          console.info(`[jsTierPump] playlist not ready yet (HTTP ${resp.status}), poll #${notReadyPolls}`, playlistUrl);
        }
        return sleep(POLL_DELAY_MS).then(tick);
      }
      return resp.text().then((text) => {
        const parsed = parsePlaylist(text, playlistUrl);
        if (parsed.mapUri && !mapUri) console.info('[jsTierPump] playlist now has #EXT-X-MAP -- device has started uploading', parsed.mapUri);
        if (parsed.mapUri) mapUri = parsed.mapUri;
        if (parsed.endlist) endlistSeen = true;
        onBufferedSeconds && onBufferedSeconds(parsed.totalDuration);

        // A sealed ('#EXT-X-ENDLIST') playlist that falls well short of the
        // segment's known full duration is NOT necessarily final: storage
        // seals a row 'partial' (which also carries ENDLIST) whenever the
        // device pauses or aborts mid-upload, but the SAME segment_uid can
        // be REOPENED later and grow to completion (vgps_storage
        // ReopenSealedDeviceRecording) -- measured 2026-09-12 as the cause
        // of a permanent "đoạn tải dở — xem được 0:17 / 4:59" freeze: the
        // pump saw a 1-chunk sealed playlist, stopped polling forever, and
        // never noticed the segment finish uploading on a later attempt.
        const shortOfExpected = expectedDurationSec > 0 && parsed.totalDuration < expectedDurationSec - 2;

        if (trackOnly) {
          if (endlistSeen && !shortOfExpected) { onEndlist && onEndlist(); return true; }
          if (stopped) return true;
          return sleep(endlistSeen ? IDLE_REPOLL_MS : POLL_DELAY_MS).then(tick);
        }

        let chain = Promise.resolve();
        if (mapUri) chain = chain.then(() => ensureInit(mapUri));
        parsed.mediaUris.forEach((uri) => {
          if (fedUris.has(uri)) return;
          fedUris.add(uri);
          chain = chain.then(() => feedMediaUri(uri).catch((e) => {
            fedUris.delete(uri);
            console.warn('[jsTierPump] chunk fetch/demux failed, will retry next poll', uri, e);
          }));
        });
        return chain.then(() => {
          const fullyFedNow = endlistSeen && fedUris.size === parsed.mediaUris.length;
          if (fullyFedNow && !shortOfExpected) {
            onEndlist && onEndlist();
            return true;
          }
          if (stopped) return true;
          // Slower once the playlist looks finished (just short) -- no need
          // to hammer storage every second waiting for a reopen that may
          // never come; still checks forever rather than giving up.
          return sleep(fullyFedNow ? IDLE_REPOLL_MS : POLL_DELAY_MS).then(tick);
        });
      });
    }, (err) => {
      // fetch() itself rejected -- a network error or a CORS block, NOT an
      // HTTP error status (those go through the `!resp.ok` branch above,
      // which retries silently by design). This branch used to retry just
      // as silently, which made a systemic problem (wrong host, CORS
      // misconfigured, dev proxy not covering this path) indistinguishable
      // from the normal "still waiting for the device" case.
      notReadyPolls += 1;
      if (notReadyPolls === 1 || notReadyPolls % 10 === 0) {
        console.warn(`[jsTierPump] playlist fetch() itself failed (poll #${notReadyPolls}) -- network error or CORS, not an HTTP status`, playlistUrl, err);
      }
      return sleep(POLL_DELAY_MS).then(tick);
    });
  }

  tick().catch((e) => { onError && onError(e); });

  return {
    stop() { stopped = true; },
  };
}
